type AttachmentCacheKey = {
  accountId: number;
  folder: string;
  uid: number;
  part: string;
};

export type AttachmentStoreRecord = AttachmentCacheKey & {
  id: string;
  savedAt: number;
  byteSize: number;
  blob: Blob;
};

const DB_NAME = "aurelia_mail_attachments_v1";
const DB_VERSION = 1;
const STORE = "attachments";
/** Aligné fenêtre cache (~100 mails × quelques PJ previewables). */
const MAX_IDB_ENTRIES = 150;
/** Quota local — LRU si dépassé (PJ previewables ≤ 10 Mo côté serveur). */
const MAX_IDB_BYTES = 256 * 1024 * 1024;

let dbPromise: Promise<IDBDatabase> | null = null;

export function attachmentStoreId(
  accountId: number,
  folder: string,
  uid: number,
  part: string
): string {
  return `${accountId}\0${folder}\0${uid}\0${part}`;
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error ?? new Error("IndexedDB indisponible."));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "id" });
        store.createIndex("accountId", "accountId", { unique: false });
        store.createIndex("savedAt", "savedAt", { unique: false });
      }
    };
  });

  return dbPromise;
}

function runTransaction<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | void
): Promise<T | void> {
  return openDb().then(
    (db) =>
      new Promise<T | void>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const request = fn(store);

        tx.oncomplete = () => {
          if (request && "result" in request) {
            resolve((request as IDBRequest<T>).result);
          } else {
            resolve();
          }
        };
        tx.onerror = () => reject(tx.error ?? new Error("Transaction IndexedDB échouée."));
        if (request) {
          request.onerror = () => reject(request.error ?? new Error("Requête IndexedDB échouée."));
        }
      })
  );
}

async function enforceIdbQuota(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const index = store.index("savedAt");
    const records: AttachmentStoreRecord[] = [];

    const cursorReq = index.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor) {
        trimRecords(store, records, resolve, reject);
        return;
      }
      records.push(cursor.value as AttachmentStoreRecord);
      cursor.continue();
    };
    cursorReq.onerror = () => reject(cursorReq.error);
    tx.onerror = () => reject(tx.error);
  });
}

function trimRecords(
  store: IDBObjectStore,
  records: AttachmentStoreRecord[],
  resolve: () => void,
  reject: (reason?: unknown) => void
): void {
  let totalBytes = records.reduce((sum, r) => sum + (r.byteSize ?? r.blob?.size ?? 0), 0);
  let count = records.length;

  if (count <= MAX_IDB_ENTRIES && totalBytes <= MAX_IDB_BYTES) {
    resolve();
    return;
  }

  records.sort((a, b) => a.savedAt - b.savedAt);
  let pending = 0;

  const tryFinish = () => {
    if (pending === 0) resolve();
  };

  for (const record of records) {
    if (count <= MAX_IDB_ENTRIES && totalBytes <= MAX_IDB_BYTES) break;
    pending += 1;
    const size = record.byteSize ?? record.blob?.size ?? 0;
    const req = store.delete(record.id);
    req.onsuccess = () => {
      count -= 1;
      totalBytes -= size;
      pending -= 1;
      tryFinish();
    };
    req.onerror = () => reject(req.error);
  }

  if (pending === 0) resolve();
}

export async function idbGetAttachment(params: AttachmentCacheKey): Promise<Blob | null> {
  const { accountId, folder, uid, part } = params;
  if (accountId <= 0 || uid <= 0 || !part) return null;

  const id = attachmentStoreId(accountId, folder, uid, part);
  const record = (await runTransaction<AttachmentStoreRecord | undefined>("readonly", (store) =>
    store.get(id)
  )) as AttachmentStoreRecord | undefined;

  return record?.blob ?? null;
}

export async function idbPutAttachment(params: AttachmentCacheKey, blob: Blob): Promise<void> {
  const { accountId, folder, uid, part } = params;
  if (accountId <= 0 || uid <= 0 || !part || blob.size <= 0) return;

  const record: AttachmentStoreRecord = {
    id: attachmentStoreId(accountId, folder, uid, part),
    accountId,
    folder,
    uid,
    part,
    savedAt: Date.now(),
    byteSize: blob.size,
    blob,
  };

  await runTransaction("readwrite", (store) => store.put(record));
  await enforceIdbQuota();
}

export async function idbClearAttachmentAccount(accountId: number): Promise<void> {
  if (accountId <= 0) return;

  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const index = store.index("accountId");
    const req = index.openCursor(IDBKeyRange.only(accountId));

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      cursor.delete();
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function idbClearAllAttachments(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    const store = tx.objectStore(STORE);
    const req = store.clear();
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
